import { forwardRef } from "react";
import { ChevronRight } from "lucide-react";
import { format } from "date-fns/format";
import { cn } from "../../../lib/utils";
import type { LabResult, LabReading, StatusIndicator } from "../types";
import { LabStatusBadge } from "../atoms/LabStatusBadge";
import { LabRangeBar } from "../atoms/LabRangeBar";
import { ReferenceRange } from "../atoms/ReferenceRange";
import { parseRefRangeToNumbers } from "../utils";

interface RefRangeDisplayProps {
  refRange: string;
  value: number;
  unit: string;
  indicator?: StatusIndicator;
  selected?: boolean;
}

const RefRangeDisplay = ({ refRange, value, unit, indicator, selected }: RefRangeDisplayProps) => {
  const refRangeParsed = parseRefRangeToNumbers(refRange);
  if (!refRangeParsed) return null;
  return (
    <div className="space-y-1">
      <LabRangeBar
        value={value}
        refRangeMin={refRangeParsed.refRangeMin}
        refRangeMax={refRangeParsed.refRangeMax}
        indicator={indicator}
        className={selected ? "opacity-70" : ""}
      />
      <ReferenceRange
        range={refRange}
        unit={unit}
        variant="short"
        className={cn(
          "text-[11px]",
          selected ? "text-gray-400 dark:text-gray-600" : "text-gray-400 dark:text-gray-500"
        )}
      />
    </div>
  );
};

export interface LabResultItemProps extends React.ComponentProps<"button"> {
  result: LabResult;
  selected?: boolean;
  displayReading?: LabReading;
}

export const LabResultItem = forwardRef<HTMLButtonElement, LabResultItemProps>(
  ({ result, selected, displayReading, className, ...props }, ref) => {
    const latest = displayReading ?? result.readings[0];

    if (!latest) {
      return null;
    }

    return (
      <button
        ref={ref}
        data-slot="lab-result-item"
        className={cn(
          "w-full text-left px-4 py-3.5 rounded-[10px] border transition-all focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
          selected
            ? "bg-gray-900 border-gray-900 text-white dark:bg-gray-100 dark:border-gray-100 dark:text-gray-900"
            : "bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-750",
          className
        )}
        {...props}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Name + category */}
            <div className="flex items-center gap-2 mb-1">
              <span
                className={cn(
                  "text-sm font-semibold truncate",
                  selected ? "text-white dark:text-gray-900" : "text-gray-900 dark:text-gray-100"
                )}
              >
                {result.name}
              </span>
              <LabStatusBadge indicator={latest.statusIndicator} className={selected ? "opacity-90" : ""} />
            </div>
            <p
              className={cn(
                "text-xs mb-2.5",
                selected ? "text-gray-400 dark:text-gray-600" : "text-gray-400 dark:text-gray-500"
              )}
            >
              {format(new Date(latest.date), "MMM d, yyyy")}
            </p>

            {/* Value (primary) */}
            <div className="flex items-baseline gap-1 mb-2.5">
              <span
                className={cn(
                  "text-2xl font-bold tabular-nums",
                  selected ? "text-white dark:text-gray-900" : "text-gray-900 dark:text-gray-100"
                )}
              >
                {latest.value}
              </span>
              <span
                className={cn(
                  "text-sm",
                  selected ? "text-gray-400 dark:text-gray-600" : "text-gray-500 dark:text-gray-400"
                )}
              >
                {latest.unit}
              </span>
            </div>

            {/* Range bar (secondary, only if refRange available) */}
            {latest.refRange && (
              <RefRangeDisplay
                refRange={latest.refRange}
                value={latest.value}
                unit={latest.unit}
                indicator={latest.statusIndicator}
                selected={selected}
              />
            )}
          </div>

          <ChevronRight
            size={16}
            className={cn(
              "mt-1 flex-shrink-0",
              selected ? "text-gray-400 dark:text-gray-600" : "text-gray-300 dark:text-gray-600"
            )}
          />
        </div>
      </button>
    );
  }
);
LabResultItem.displayName = "LabResultItem";
